import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { Card, Form, Button, Alert, Row, Col } from 'react-bootstrap';
import FalconCardHeader from '../../../common/FalconCardHeader';

const ProfileSettings = ({ user, onUpdate, updating }) => {
  // Inicializar configuraciones desde el usuario
  const [notificationSettings, setNotificationSettings] = useState({
    emailNotifications: user?.settings?.notifications?.emailNotifications ?? true,
    systemNotifications: user?.settings?.notifications?.systemNotifications ?? true,
    budgetAlerts: user?.settings?.notifications?.budgetAlerts ?? true,
    campaignAlerts: user?.settings?.notifications?.campaignAlerts ?? true,
    weeklyReports: user?.settings?.notifications?.weeklyReports ?? false,
  });

  const [uiPreferences, setUiPreferences] = useState({
    language: user?.settings?.ui?.language ?? 'es',
    timezone: user?.settings?.ui?.timezone ?? 'America/Bogota',
    dateFormat: user?.settings?.ui?.dateFormat ?? 'DD/MM/YYYY',
    currency: user?.settings?.ui?.currency ?? 'COP',
    theme: user?.settings?.ui?.theme ?? 'light',
  });

  const [dashboardSettings, setDashboardSettings] = useState({
    defaultView: user?.settings?.dashboard?.defaultView ?? 'overview',
    chartsToShow: user?.settings?.dashboard?.chartsToShow ?? ['budget', 'spend', 'performance'],
    refreshInterval: user?.settings?.dashboard?.refreshInterval ?? 300000,
    autoRefresh: user?.settings?.dashboard?.autoRefresh ?? true,
  });

  const [hasChanges, setHasChanges] = useState(false);

  const handleNotificationChange = (key, value) => {
    setNotificationSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleUiChange = (key, value) => {
    setUiPreferences(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleDashboardChange = (key, value) => {
    setDashboardSettings(prev => ({
      ...prev,
      [key]: value
    }));
    setHasChanges(true);
  };

  const handleSaveSettings = async () => {
    const updateData = {
      notificationSettings,
      uiPreferences,
      dashboardSettings
    };
    
    const success = await onUpdate(updateData);
    if (success) {
      setHasChanges(false);
    }
  };

  const handleResetSettings = () => {
    setNotificationSettings({
      emailNotifications: user?.settings?.notifications?.emailNotifications ?? true,
      systemNotifications: user?.settings?.notifications?.systemNotifications ?? true,
      budgetAlerts: user?.settings?.notifications?.budgetAlerts ?? true,
      campaignAlerts: user?.settings?.notifications?.campaignAlerts ?? true,
      weeklyReports: user?.settings?.notifications?.weeklyReports ?? false,
    });
    
    setUiPreferences({
      language: user?.settings?.ui?.language ?? 'es',
      timezone: user?.settings?.ui?.timezone ?? 'America/Bogota',
      dateFormat: user?.settings?.ui?.dateFormat ?? 'DD/MM/YYYY',
      currency: user?.settings?.ui?.currency ?? 'COP',
      theme: user?.settings?.ui?.theme ?? 'light',
    });

    setDashboardSettings({
      defaultView: user?.settings?.dashboard?.defaultView ?? 'overview',
      chartsToShow: user?.settings?.dashboard?.chartsToShow ?? ['budget', 'spend', 'performance'],
      refreshInterval: user?.settings?.dashboard?.refreshInterval ?? 300000,
      autoRefresh: user?.settings?.dashboard?.autoRefresh ?? true,
    });
    
    setHasChanges(false);
  };

  return (
    <Card>
      <FalconCardHeader title="Configuraciones" />
      
      <Card.Body className="bg-body-tertiary">
        {/* Notificaciones */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <FontAwesomeIcon icon="bell" className="me-1" />
            Notificaciones
          </h6>
          
          <Form.Check
            type="switch"
            id="emailNotifications"
            label="Notificaciones por email"
            checked={notificationSettings.emailNotifications}
            onChange={(e) => handleNotificationChange('emailNotifications', e.target.checked)}
            className="mb-2"
          />
          
          <Form.Check
            type="switch"
            id="systemNotifications"
            label="Notificaciones del sistema"
            checked={notificationSettings.systemNotifications}
            onChange={(e) => handleNotificationChange('systemNotifications', e.target.checked)}
            className="mb-2"
          />

          <Form.Check
            type="switch"
            id="budgetAlerts"
            label="Alertas de presupuesto"
            checked={notificationSettings.budgetAlerts}
            onChange={(e) => handleNotificationChange('budgetAlerts', e.target.checked)}
            className="mb-2"
          />

          <Form.Check
            type="switch"
            id="campaignAlerts"
            label="Alertas de campañas"
            checked={notificationSettings.campaignAlerts}
            onChange={(e) => handleNotificationChange('campaignAlerts', e.target.checked)}
            className="mb-2"
          />

          <Form.Check
            type="switch"
            id="weeklyReports"
            label="Reportes semanales"
            checked={notificationSettings.weeklyReports}
            onChange={(e) => handleNotificationChange('weeklyReports', e.target.checked)}
            className="mb-2"
          />
        </div>

        {/* Preferencias de Interfaz */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <FontAwesomeIcon icon="palette" className="me-1" />
            Interfaz de Usuario
          </h6>
          
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Idioma</Form.Label>
                <Form.Select
                  value={uiPreferences.language}
                  onChange={(e) => handleUiChange('language', e.target.value)}
                >
                  <option value="es">Español</option>
                  <option value="en">English</option>
                  <option value="pt">Português</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Zona Horaria</Form.Label>
                <Form.Select
                  value={uiPreferences.timezone}
                  onChange={(e) => handleUiChange('timezone', e.target.value)}
                >
                  <option value="America/Bogota">Bogotá (GMT-5)</option>
                  <option value="America/Mexico_City">Ciudad de México (GMT-6)</option>
                  <option value="America/Lima">Lima (GMT-5)</option>
                  <option value="America/Santiago">Santiago (GMT-3)</option>
                  <option value="America/Buenos_Aires">Buenos Aires (GMT-3)</option>
                  <option value="Europe/Madrid">Madrid (GMT+1)</option>
                  <option value="America/New_York">Nueva York (GMT-5)</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Formato de Fecha</Form.Label>
                <Form.Select
                  value={uiPreferences.dateFormat}
                  onChange={(e) => handleUiChange('dateFormat', e.target.value)}
                >
                  <option value="DD/MM/YYYY">DD/MM/YYYY</option>
                  <option value="MM/DD/YYYY">MM/DD/YYYY</option>
                  <option value="YYYY-MM-DD">YYYY-MM-DD</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Moneda</Form.Label>
                <Form.Select
                  value={uiPreferences.currency}
                  onChange={(e) => handleUiChange('currency', e.target.value)}
                >
                  <option value="COP">COP - Peso Colombiano</option>
                  <option value="USD">USD - Dólar Americano</option>
                  <option value="EUR">EUR - Euro</option>
                  <option value="MXN">MXN - Peso Mexicano</option>
                </Form.Select>
              </Form.Group>
            </Col>
          </Row>
        </div>

        {/* Configuraciones del Dashboard */}
        <div className="mb-4">
          <h6 className="text-muted mb-3">
            <FontAwesomeIcon icon="tachometer-alt" className="me-1" />
            Dashboard
          </h6>
          
          <Row className="g-3">
            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Vista por Defecto</Form.Label>
                <Form.Select
                  value={dashboardSettings.defaultView}
                  onChange={(e) => handleDashboardChange('defaultView', e.target.value)}
                >
                  <option value="overview">Vista General</option>
                  <option value="budget">Control de Presupuesto</option>
                  <option value="campaigns">Campañas</option>
                  <option value="analytics">Analíticas</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col md={6}>
              <Form.Group>
                <Form.Label className="fw-semibold">Intervalo de Actualización</Form.Label>
                <Form.Select
                  value={dashboardSettings.refreshInterval}
                  onChange={(e) => handleDashboardChange('refreshInterval', parseInt(e.target.value))}
                >
                  <option value={60000}>1 minuto</option>
                  <option value={300000}>5 minutos</option>
                  <option value={600000}>10 minutos</option>
                  <option value={1800000}>30 minutos</option>
                  <option value={0}>Manual</option>
                </Form.Select>
              </Form.Group>
            </Col>

            <Col xs={12}>
              <Form.Check
                type="switch"
                id="autoRefresh"
                label="Actualización automática"
                checked={dashboardSettings.autoRefresh}
                onChange={(e) => handleDashboardChange('autoRefresh', e.target.checked)}
                className="mb-2"
              />
            </Col>
          </Row>
        </div>

        {/* Información adicional */}
        <Alert variant="light" className="mb-3">
          <small className="text-muted">
            <FontAwesomeIcon icon="info-circle" className="me-1" />
            Las configuraciones se guardan en tu perfil y se sincronizarán entre dispositivos.
          </small>
        </Alert>

        {/* Botones de acción */}
        {hasChanges && (
          <div className="d-flex gap-2">
            <Button 
              variant="primary" 
              size="sm"
              onClick={handleSaveSettings}
              disabled={updating}
            >
              {updating ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Guardando...</span>
                  </div>
                  Guardando...
                </>
              ) : (
                <>
                  <FontAwesomeIcon icon="save" className="me-1" />
                  Guardar Configuraciones
                </>
              )}
            </Button>
            
            <Button 
              variant="outline-secondary" 
              size="sm"
              onClick={handleResetSettings}
              disabled={updating}
            >
              <FontAwesomeIcon icon="undo" className="me-1" />
              Restablecer
            </Button>
          </div>
        )}
      </Card.Body>
    </Card>
  );
};

export default ProfileSettings; 